// Snapshot interpolation buffer для отображения чужих игроков с плавностью.
// Держим короткую историю снапшотов и рендерим время на ~100мс позади.

import type { Snapshot, SnapshotPlayer } from "../net/protocol";
import { lerp, lerpAngle } from "./math";

export const RENDER_DELAY_MS = 100;

interface BufferedSnap {
  arrivedAt: number;  // performance.now()
  serverTick: number;
  players: Map<number, SnapshotPlayer>;
}

/** Пул Map для снапшотов — ~20 Гц × new Map давали лишний GC на длинных сессиях. */
const playerMapPool: Map<number, SnapshotPlayer>[] = [];

function takePlayerMap(): Map<number, SnapshotPlayer> {
  const m = playerMapPool.pop();
  if (m) {
    m.clear();
    return m;
  }
  return new Map();
}

function releasePlayerMap(m: Map<number, SnapshotPlayer>): void {
  playerMapPool.push(m);
}

export class SnapshotBuffer {
  private buf: BufferedSnap[] = [];
  private maxLen = 32;

  /** Самый свежий снапшот игрока (для bodyVox/hits — без «отката» к старому буферу). */
  latestPlayer(playerId: number): SnapshotPlayer | null {
    if (this.buf.length === 0) return null;
    return this.buf[this.buf.length - 1].players.get(playerId) ?? null;
  }

  /** Подмешивает авторитетные поля урона из последнего снапшота (поза остаётся от base). */
  private latestAuthoritativePlayer(playerId: number): SnapshotPlayer | null {
    for (let i = this.buf.length - 1; i >= 0; i--) {
      const p = this.buf[i].players.get(playerId);
      if (p) return p;
    }
    return null;
  }

  private withAuthoritativeDamage(playerId: number, base: SnapshotPlayer | null): SnapshotPlayer | null {
    if (!base || this.buf.length === 0) return base;
    const latest = this.latestAuthoritativePlayer(playerId);
    if (!latest) return base;
    return {
      ...base,
      bodyVox: latest.bodyVox,
      bodyVoxShell: latest.bodyVoxShell,
      hitCount: latest.hitCount,
      hits: latest.hits,
      hp: latest.hp,
      score: latest.score,
      shotHits: latest.shotHits,
      deaths: latest.deaths,
      activeAmmo: latest.activeAmmo,
    };
  }

  push(s: Snapshot, now: number): void {
    const players = takePlayerMap();
    for (const p of s.players) players.set(p.id, p);
    this.buf.push({ arrivedAt: now, serverTick: s.tick, players });
    if (this.buf.length > this.maxLen) {
      const dropped = this.buf.shift();
      if (dropped) releasePlayerMap(dropped.players);
    }
  }

  clear(): void {
    for (const b of this.buf) releasePlayerMap(b.players);
    this.buf.length = 0;
  }

  /** Вернуть состояние игрока в момент (now - delay). null если нет данных. */
  sample(playerId: number, now: number): SnapshotPlayer | null {
    if (this.buf.length === 0) return null;
    const target = now - RENDER_DELAY_MS;
    let a = this.buf[0];
    let b = this.buf[this.buf.length - 1];
    if (target <= a.arrivedAt) {
      return this.withAuthoritativeDamage(playerId, a.players.get(playerId) ?? null);
    }
    if (target >= b.arrivedAt) {
      return this.withAuthoritativeDamage(playerId, b.players.get(playerId) ?? null);
    }

    for (let i = 0; i < this.buf.length - 1; i++) {
      const s0 = this.buf[i];
      const s1 = this.buf[i + 1];
      if (target >= s0.arrivedAt && target <= s1.arrivedAt) {
        a = s0; b = s1;
        const t = (target - s0.arrivedAt) / Math.max(s1.arrivedAt - s0.arrivedAt, 1);
        const pa = s0.players.get(playerId);
        const pb = s1.players.get(playerId);
        if (!pa) return this.withAuthoritativeDamage(playerId, pb ?? null);
        if (!pb) return this.withAuthoritativeDamage(playerId, pa);
        const blended: SnapshotPlayer = {
          id: playerId,
          x: lerp(pa.x, pb.x, t),
          y: lerp(pa.y, pb.y, t),
          z: lerp(pa.z, pb.z, t),
          yaw: lerpAngle(pa.yaw, pb.yaw, t),
          hitCount: pb.hitCount,
          hits: pb.hits,
          hp: pb.hp,
          score: pb.score,
          shotHits: pb.shotHits,
          deaths: pb.deaths,
          activeAmmo: pb.activeAmmo,
          team: pb.team,
          roundWins: pb.roundWins,
          bodyVox: pb.bodyVox,
          bodyVoxShell: pb.bodyVoxShell,
          charId: pb.charId, // characters
          maxHp: pb.maxHp,   // characters
          weapon: pb.weapon,           // weapons
          weaponFlags: pb.weaponFlags, // weapons
        };
        return this.withAuthoritativeDamage(playerId, blended);
      }
    }
    return this.withAuthoritativeDamage(playerId, b.players.get(playerId) ?? null);
  }

  /** Список ID игроков, известных в самом свежем снапшоте. */
  latestPlayerIds(): number[] {
    if (this.buf.length === 0) return [];
    return [...this.buf[this.buf.length - 1].players.keys()];
  }
}
